/**
 * Processing health (design-reference §4.16): a status banner, the queue/DLQ
 * metrics row and the failed-documents (DLQ) table with per-message replay and
 * discard. Every number is a real queue depth or DLQ field from the API.
 */
import { useState } from 'react';

import { Button, ConfirmModal, ErrorState, Skeleton, useToast } from '../../components';
import type { DlqMessageView } from '../../api/schema';

import { useAdminApi } from './api';
import { fmtInt, fmtTimestamp } from './format';
import { useHealth, type HealthData } from './useAdmin';

import './admin.css';

interface WorkQueue {
  name: string;
  depth: number;
}

function readQueues(raw: Record<string, unknown>[]): WorkQueue[] {
  return raw.map((q) => ({
    name: typeof q.name === 'string' ? q.name : '—',
    depth: Number(q.depth ?? 0),
  }));
}

function HealthBody({ data, reload }: { data: HealthData; reload: () => void }): React.JSX.Element {
  const { toast } = useToast();
  const api = useAdminApi();
  const [pending, setPending] = useState<string | null>(null);
  const [discardTarget, setDiscardTarget] = useState<DlqMessageView | null>(null);

  const dlqEmpty = data.health.total_dead_lettered === 0 && data.dlq.length === 0;
  const queues = readQueues(data.health.work_queues);

  const replay = async (m: DlqMessageView): Promise<void> => {
    setPending(m.message_id);
    try {
      await api.dlq.replay(m.stage, m.message_id);
      toast(`Replayed to q.${m.stage} — processing resumed`);
      reload();
    } catch {
      toast('Replay failed — the message stays in the DLQ', { tone: 'error' });
    } finally {
      setPending(null);
    }
  };

  const discard = async (m: DlqMessageView): Promise<void> => {
    setPending(m.message_id);
    try {
      await api.dlq.discard(m.stage, m.message_id);
      toast('Message discarded — document marked FAILED, audit written');
      setDiscardTarget(null);
      reload();
    } catch {
      toast('Discard failed — the message stays in the DLQ', { tone: 'error' });
    } finally {
      setPending(null);
    }
  };

  return (
    <div className="adm-screen">
      {dlqEmpty ? (
        <div className="adm-banner adm-banner--ok" role="status">
          <span className="adm-dot adm-dot--ok" aria-hidden="true" />
          Pipeline healthy — all queues nominal, dead-letter queue empty.
        </div>
      ) : (
        <div className="adm-banner adm-banner--warn" role="status">
          <span className="adm-dot adm-dot--warn" aria-hidden="true" />
          {fmtInt(data.health.total_dead_lettered)}{' '}
          {data.health.total_dead_lettered === 1 ? 'message' : 'messages'} in the dead-letter queue
          {' '}
          {data.health.total_dead_lettered === 1 ? 'needs' : 'need'} attention — replay or discard below.
        </div>
      )}

      <section className="adm-metrics" aria-label="Processing metrics">
        <div className="adm-card adm-card--pad">
          <h3 className="adm-eyebrow">Queue depths</h3>
          <ul className="adm-queues">
            {queues.map((q) => (
              <li key={q.name} className="adm-queue">
                <span className="adm-queue__name">{q.name}</span>
                <span className="adm-queue__track">
                  <span
                    className={`adm-queue__fill ${q.depth === 0 ? 'is-empty' : ''}`}
                    style={{ width: `${String(Math.min(100, q.depth * 7))}%` }}
                  />
                </span>
                <span className="adm-queue__depth">{fmtInt(q.depth)}</span>
              </li>
            ))}
            {queues.length === 0 ? <li className="adm-muted">No work queues reported.</li> : null}
          </ul>
          <p className="adm-note">KEDA scales workers on backlog — q.ocr &gt; 200 adds OCR replicas.</p>
        </div>

        <div className="adm-card adm-card--pad adm-tile">
          <span className="adm-tile__label">Total backlog</span>
          <span className="adm-tile__value">{fmtInt(data.health.total_backlog)}</span>
          <span className="adm-tile__note">messages queued across all stages</span>
        </div>

        <div className="adm-card adm-card--pad adm-tile">
          <span className="adm-tile__label">Dead-lettered</span>
          <span className="adm-tile__value">{fmtInt(data.health.total_dead_lettered)}</span>
          <span className="adm-tile__note">poison messages awaiting a decision</span>
        </div>
      </section>

      <section className="adm-card" aria-labelledby="adm-dlq-h">
        <header className="adm-card__head">
          <h2 id="adm-dlq-h" className="adm-card__title">
            Failed documents (DLQ)
          </h2>
          <span className="adm-card__sub">exhausted retries: 30s → 2m → 10m → 30m ×5</span>
        </header>

        {data.dlq.length === 0 ? (
          <p className="adm-dlq-empty">
            Nothing here — poison messages land in per-stage DLQs and appear in this table for manual
            replay.
          </p>
        ) : (
          <div className="adm-dlq" role="table" aria-label="Failed documents">
            <div className="adm-dlq__row adm-dlq__head" role="row">
              <span role="columnheader">Document</span>
              <span role="columnheader">Stage</span>
              <span role="columnheader">Attempts</span>
              <span role="columnheader">Error</span>
              <span role="columnheader">When</span>
              <span role="columnheader" className="sr-only">
                Actions
              </span>
            </div>
            {data.dlq.map((m) => (
              <div key={m.message_id} className="adm-dlq__row" role="row">
                <span role="cell" className="adm-mono adm-dlq__doc">
                  {m.document_id ?? m.message_id}
                </span>
                <span role="cell" className="adm-muted">
                  {m.stage.toUpperCase()}
                </span>
                <span role="cell" className="adm-num">
                  {fmtInt(m.attempt)} attempts
                </span>
                <span role="cell" className="adm-dlq__err">
                  {m.reason ?? 'Unknown error'}
                </span>
                <span role="cell" className="adm-dlq__ts">
                  {fmtTimestamp(m.occurred_at)}
                </span>
                <span role="cell" className="adm-dlq__actions">
                  <Button
                    size="sm"
                    onClick={() => {
                      void replay(m);
                    }}
                    loading={pending === m.message_id}
                    disabled={pending !== null}
                  >
                    Replay
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => {
                      setDiscardTarget(m);
                    }}
                    disabled={pending !== null}
                  >
                    Discard
                  </Button>
                </span>
              </div>
            ))}
          </div>
        )}
      </section>

      <ConfirmModal
        open={discardTarget !== null}
        destructive
        title="Discard this message?"
        confirmLabel="Discard"
        cancelLabel="Cancel"
        loading={pending !== null}
        onCancel={() => {
          setDiscardTarget(null);
        }}
        onConfirm={() => {
          if (discardTarget !== null) void discard(discardTarget);
        }}
      >
        The document is marked <strong>FAILED</strong> and an audit entry is written. This cannot be
        undone — replay instead if you want to retry processing.
      </ConfirmModal>
    </div>
  );
}

export function HealthScreen(): React.JSX.Element {
  const state = useHealth();

  if (state.status === 'loading') {
    return (
      <div className="adm-screen" aria-busy="true">
        <Skeleton height={56} />
        <Skeleton height={160} />
        <Skeleton height={220} />
      </div>
    );
  }
  if (state.status === 'error') {
    return (
      <div className="adm-screen">
        <ErrorState
          title="Couldn't load processing health"
          body="The queue and dead-letter data failed to load."
          traceId={state.traceId ?? undefined}
          actions={
            <Button variant="secondary" onClick={state.reload}>
              Retry
            </Button>
          }
        />
      </div>
    );
  }
  return <HealthBody data={state.data} reload={state.reload} />;
}
